import { isEqual } from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@fluentui/react';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useP$DaterangeDefault, ConversionGoalKeyToStringMapping } from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  ConversionGoalSelector,
} from '@bingads-webui-campaign-react/goal-selector';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { paginationStyle } from '@bingads-webui-campaign-react/common-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

export const getConversionGoalStyles = (_, theme) => ({
  ConversionGoalSelectorContainer: {
    ...paginationStyle(theme),
    '& .list-selector-container': {
      width: 468,
    },
  },
});

const ConversionGoalsWithCustomValidation = ({ validationProps, updateValidation, ...restProps }) => {
  const { data: conversionGoalsData } = validationProps;
  const { conversionGoals = {} } = conversionGoalsData;
  const i18n = useContext(I18nContext);

  const classes = useDefaultStyles(getConversionGoalStyles);

  const activity = useContext(ActivityContext);

  const legacyI18n = useMemo(() => buildLegacyI18n(
    i18n,
    [ConversionGoalKeyToStringMapping]
  ), [i18n]);
  const $dataRange = useP$DaterangeDefault();

  const onChange = useCallback((changedData) => {
    const newData = {
      conversionGoals: {
        ...conversionGoalsData.conversionGoals,
        ...changedData,
      },
    };

    updateValidation([], isEqual(newData, conversionGoalsData) ? conversionGoalsData : newData);

    if (activity) {
      activity.signal(`onConversionGoalsChange - ${JSON.stringify(newData)}`);
    }
  }, [conversionGoalsData, updateValidation, activity]);
  const campaignConversionGoal = useMemo(() => ({
    Goals: conversionGoals.originalConversionGoals,
  }), [conversionGoals.originalConversionGoals]);

  return (conversionGoals && conversionGoals.goalScopeSelection ?
    <div className={classes.ConversionGoalSelectorContainer}>
      <div className="conversion-goal-label-container" style={{ marginBottom: 10 }}>
        <Text>{i18n.getString(_TL_('Select which conversions are included in the Conversion columns for this campaign and used for optimizing your bid strategy. Updating this setting will override the “Include in conversions” setting made at the account level.'))}</Text>
        <LearnMoreLink
          linkText={i18n.getString(_TL_('Learn more'))}
          topic="app54814"
        />
      </div>
      <ConversionGoalSelector
        {...restProps}
        i18n={legacyI18n}
        isSelectorEnabled
        pageSize={10}
        isMultiMode
        isHideSaveBar
        $dataRange={$dataRange}
        goalSelection={conversionGoals.goalSelection}
        campaignConversionGoal={campaignConversionGoal}
        goalScopeSelection={conversionGoals.goalScopeSelection}
        onChange={onChange}
        activityLogger={activity}
      />
    </div> : null
  );
};

ConversionGoalsWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      conversionGoals: PropTypes.shape({
        goalSelection: PropTypes.arrayOf(PropTypes.shape({})),
        goalScopeSelection: PropTypes.string,
        originalConversionGoals: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  showTrackingStatusIcon: PropTypes.bool, // included in restProps
};

ConversionGoalsWithCustomValidation.defaultProps = {
  showTrackingStatusIcon: false,
};

export const ValidatedConversionGoals = withCustomValidation(ConversionGoalsWithCustomValidation);
